import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JournalEntry } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { payeeName } from "../../shared/lib/format";
import { Modal } from "../../shared/ui/Modal";
import { assignTransaction, unassignTransaction } from "./api";
import { PlanningTargetPicker, type PlanningTarget } from "./PlanningTargetPicker";
import { usePlanningTargets } from "./usePlanningTargets";

interface TransactionPlanningDialogProps {
  open: boolean;
  onClose: () => void;
  bookID: string;
  entry: JournalEntry;
  onNotify: (message: string) => void;
}

// Assignment from the transaction's side: this fact, one owner. The dialog
// states the owner it already has, because replacing one is a different act
// from giving it its first, and the API is told so explicitly.
export function TransactionPlanningDialog({ open, onClose, bookID, entry, onNotify }: TransactionPlanningDialogProps) {
  const queryClient = useQueryClient();
  const targets = usePlanningTargets(bookID);
  const current = entry.planningAssignment ?? null;
  const [target, setTarget] = useState<PlanningTarget>({ kind: current?.type ?? "item", id: current?.id ?? "" });
  const [failure, setFailure] = useState("");

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    await queryClient.invalidateQueries({ queryKey: ["transactions"] });
    await queryClient.invalidateQueries({ queryKey: ["planning"] });
    await queryClient.invalidateQueries({ queryKey: ["plan-groups"] });
  };

  const assign = useMutation({
    mutationFn: () => assignTransaction(bookID, entry.transaction.id, {
      // Exactly one id travels, and replacing an existing owner is asked for
      // by name rather than happening as a side effect.
      planItemId: target.kind === "item" ? target.id : undefined,
      planGroupId: target.kind === "group" ? target.id : undefined,
      replace: current !== null,
    }),
    onSuccess: async () => {
      await refresh();
      onNotify("Planning assignment saved. The journal entry is unchanged.");
      onClose();
    },
    onError: (error) => setFailure(humanError(error)),
  });

  const remove = useMutation({
    mutationFn: () => unassignTransaction(bookID, entry.transaction.id),
    onSuccess: async () => {
      await refresh();
      onNotify("The transaction no longer belongs to a plan. Its journal entry is unchanged.");
      onClose();
    },
    onError: (error) => setFailure(humanError(error)),
  });

  const description = payeeName(entry.transaction.description || "Transaction");
  const unchanged = current !== null && current.type === target.kind && current.id === target.id;

  return (
    <Modal
      open={open} onClose={onClose} title="Planning" subtitle={description}
      footer={<>
        <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
        {current ? (
          <button className="secondary-button danger" type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? "Removing…" : "Not assigned"}
          </button>
        ) : null}
        <button className="primary-button compact" type="button"
          disabled={assign.isPending || target.id === "" || unchanged} onClick={() => { setFailure(""); assign.mutate(); }}>
          <span>{assign.isPending ? "Saving…" : current ? "Replace target" : "Assign"}</span><span aria-hidden="true">→</span>
        </button>
      </>}
    >
      <dl className="pick-total">
        <div>
          <dt>Currently</dt>
          <dd>{current ? `${current.type === "group" ? "Group" : "Item"} — ${current.name}` : "Not assigned"}</dd>
        </div>
      </dl>
      {targets.isPending ? <p className="panel-note">Loading plans and groups…</p> : null}
      {targets.isError ? <p className="form-error" role="alert">{humanError(targets.error)}</p> : null}
      {targets.data ? (
        <PlanningTargetPicker
          items={targets.data.items} groups={targets.data.groups}
          value={target} onChange={(next) => { setFailure(""); setTarget(next); }}
        />
      ) : null}
      {current ? (
        <p className="panel-note">
          This transaction already belongs to {current.type === "group" ? "a group" : "a plan item"}. Saving moves it;
          a transaction has one planning owner at most, so it cannot belong to both.
        </p>
      ) : null}
      {failure ? <p className="form-error" role="alert">{failure}</p> : null}
    </Modal>
  );
}
